import { ActionIcon, Text, Tooltip } from '@mantine/core'
import { Mail, Phone, Printer, RefreshCw, X } from 'lucide-react'
import type { EmploymentVerificationAction } from 'types/graphql'

import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import {
  VoEActionStatus,
  VoEOutreachType,
} from 'src/graphql/types/verificationOfEmployments'
import { formatDateCustom } from 'src/lib/formatters'

import {
  ACTOR_MARKER_CLASS,
  AUDIT_TRAIL_TIMESTAMP_CLASS,
  AUTOMATED_OUTREACH_LABEL,
  isAutomatedAction,
  OUTREACH_DATE_TIME_FORMAT,
  typeLabel,
} from './outreachActionLabels'

const TYPE_ICON: Record<VoEOutreachType, React.ReactNode> = {
  [VoEOutreachType.EMAIL]: <Mail size={12} />,
  [VoEOutreachType.CALL]: <Phone size={12} />,
  [VoEOutreachType.FAX]: <Printer size={12} />,
}

const DASHED_RAIL =
  'repeating-linear-gradient(to bottom, #2E90FA 0 3px, #ffffff 3px 6px)'

interface UpcomingOutreachRowProps {
  action: EmploymentVerificationAction
  contact: string | null
  onReschedule: (action: EmploymentVerificationAction) => void
  onCancel: (actionId: string) => void
  cancelling: boolean
}

/**
 * The next pending attempt, drawn as an audit-trail row with a blue band and a
 * dashed bullet so it reads as "not yet" against the completed rows below.
 */
const UpcomingOutreachRow = ({
  action,
  contact,
  onReschedule,
  onCancel,
  cancelling,
}: UpcomingOutreachRowProps) => {
  const automated = isAutomatedAction(action)
  // Only a row the runner has not picked up yet can be moved or cancelled.
  const pending = action.status === VoEActionStatus.SCHEDULED
  return (
    <div
      data-testid="upcoming-outreach-row"
      className="relative -my-1.5 -ml-5 -mr-2 flex gap-2.5 rounded-md bg-doubleNickel-blue-50 py-1.5 pl-5 pr-2"
    >
      <div
        aria-hidden="true"
        className="absolute -bottom-1.5 left-[15px] top-[28px] z-10 w-px"
        style={{ background: DASHED_RAIL }}
      />
      <div className="relative z-10 -ml-4 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border border-dashed border-doubleNickel-blue-500 bg-white">
        <span className="text-doubleNickel-blue-500">
          {TYPE_ICON[action.type as VoEOutreachType] ??
            TYPE_ICON[VoEOutreachType.EMAIL]}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex h-[22px] items-center justify-between gap-2">
          <Text size="xs" fw={500} className="text-doubleNickel-gray-900">
            {typeLabel(action.type)} {pending ? 'scheduled' : 'in progress'}
          </Text>
          <div className="flex items-center gap-1">
            {automated && (
              <ActorBadge
                variant="auto"
                label={AUTOMATED_OUTREACH_LABEL}
                className={ACTOR_MARKER_CLASS}
              />
            )}
            <Text size="xs" className={AUDIT_TRAIL_TIMESTAMP_CLASS}>
              {formatDateCustom(action.scheduledAt, OUTREACH_DATE_TIME_FORMAT)}
            </Text>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Text size="xs" className="text-doubleNickel-gray-600">
            {contact ? `To ${contact}` : ''}
          </Text>
          {pending && (
            <div className="-my-1 flex shrink-0 items-center gap-0.5">
              <Tooltip label="Reschedule">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Reschedule"
                  onClick={() => onReschedule(action)}
                >
                  <RefreshCw
                    size={12}
                    className="stroke-doubleNickel-gray-500"
                  />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Cancel">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Cancel"
                  loading={cancelling}
                  onClick={() => onCancel(action.voeActionId)}
                >
                  <X size={12} className="stroke-doubleNickel-gray-500" />
                </ActionIcon>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UpcomingOutreachRow
